import {
  Currencies,
  FeatureFlagTypes,
  type WorkflowCompletion,
} from '@generated/graphql';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { isEftFormEnabled } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositAccountRestrictions';
import type { useDepositSchedule } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/useDepositSchedule';
import { withdrawSubAccountFilter } from '@ovComponents/4-module/workflowCompletion/subSteps/withdrawForm/withdrawSubAccountFilter';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { UserContext } from '@providers/userContextProvider';
import type { ConfigType } from 'dayjs';
import { useContext, useState } from 'react';
import { useParams } from 'react-router';
import { useOrganizationFeatureFlag } from '../../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../../hooks/usePermission';

type UseDepositEftProp = {
  deposit: TransferContextData;
  userId?: string;
  workflowCompletion?: WorkflowCompletion;
  schedule: Pick<ReturnType<typeof useDepositSchedule>, 'changeScheduledDate'>;
};

export const useDepositEft = ({
  deposit,
  userId,
  workflowCompletion,
  schedule,
}: UseDepositEftProp) => {
  const { userId: paramsUserId } = useParams();
  const { activeOrganization, custodianConnection } = useContext(UserContext);
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');
  const enableMultiCurrencyDepositTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyDepositTransfers,
  );
  const [dateHasChanged, setDateHasChanged] = useState(false);

  const subAccountFilter = withdrawSubAccountFilter({
    contextObjectType: deposit?.objectType,
    contextObjectId: deposit?.objectId,
    completionObjectType: workflowCompletion?.objectType,
    completionObjectId: workflowCompletion?.objectId,
    userId,
  });
  const canQueryBankAccounts = Boolean(paramsUserId || deposit?.subAccount?.id);
  const amountCurrency = enableMultiCurrencyDepositTransfers
    ? deposit?.bankAccount?.currency ||
      deposit?.subAccount?.account?.baseCurrency ||
      activeOrganization?.productShelfCurrencies?.[0] ||
      Currencies.Cad
    : undefined;

  const isFormEnabledFor = (subAccount: Pick<SubAccount, 'account'>) =>
    isEftFormEnabled({
      selectedSubAccount: subAccount,
      applicableData: deposit,
    });

  const changeScheduledDate = async (date: ConfigType) => {
    setDateHasChanged(true);
    await schedule.changeScheduledDate(date);
  };

  return {
    enableMultiCurrencyDepositTransfers,
    subAccountFilter,
    bankAccountsQueryUserId: canQueryBankAccounts ? (userId ?? '') : '',
    isInitiatedByRepresentative:
      canWriteTransferOboClients && !canWriteTransferBasic,
    amountCurrency,
    includeCashHoldings: custodianConnection?.allowCashAsDepositTarget,
    isFormEnabledFor,
    dateHasChanged,
    changeScheduledDate,
  };
};
